import { useState, useEffect } from 'react';
import { apiInstance } from '../api/apiInstance';
import { DashboardData, formatData } from '../pages/dashboard/util';
import { DashboardFilters } from '../utils/constant';
import { downloadFile } from 'src/utils/utility-functions';

interface UseTableProps {
  activityType?: 'recent' | 'history';
  role: string;
  filters?: DashboardFilters;
}

const generateQueryParams = (filters?: DashboardFilters): string => {
  let queryParams = '';

  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          value.forEach((v) => (queryParams += `&${key}=${v}`));
        } else if (key === 'searchValue') {
          queryParams += `&searchKey=trackingNumber&searchKey=case_numbers&searchKey=debtorName&searchValue=${value}`;
        } else if (key === 'printGenStatus') {
          queryParams += `&printGenStatus=${value}`;
        } else {
          queryParams += `&${key}=${value}`;
        }
      }
    });
  }
  return queryParams;
};

const buildRequestUrl = (baseUrl: string, filters?: DashboardFilters) => {
  const params = generateQueryParams(filters);
  const requestUrl = params ? `${baseUrl}${params}` : baseUrl;
  return requestUrl;
};

const useTable = ({ activityType, filters, role }: UseTableProps) => {
  const [data, setData] = useState<DashboardData[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);

  const [error, setError] = useState<boolean>(false);

  const exportData = async (type: 'csv' | 'invoice') => {
    setExportAnchor(null);
    const requestUrl = buildRequestUrl(
      `/job/${type === 'csv' ? 'export' : 'export-invoices'}?unitType=${role === 'admin' ? 'organization' : 'user'}&activityType=${activityType}`,
      filters,
    );
    try {
      await downloadFile(requestUrl, `${Date.now()}_${type === 'csv' ? 'dashboard' : 'invoice'}`);
    } catch (err: any) {
      setErrorMsg(err.message);
    }
  };

  const exportItems = [
    { label: 'Export CSV', onClick: () => exportData('csv') },
    { label: 'Export Invoice', onClick: () => exportData('invoice') },
  ];

  const fetchData = async () => {
    const skip = page * rowsPerPage;
    const take = rowsPerPage;

    const requestUrl = buildRequestUrl(
      `/job?skip=${skip}&take=${take}&unitType=${role === 'admin' ? 'organization' : 'user'}&activityType=${activityType}`,
      filters,
    );

    try {
      const response = await apiInstance.get(requestUrl);
      setData(formatData(response.data.items));
      setTotalCount(response.data.totalCount);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const updateData = (data: any) => {
    setData(data);
  };

  const openExportButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExportAnchor(event.currentTarget);
  };

  const closeExportButton = () => {
    setExportAnchor(null);
  };

  const clearErrorMsg = () => {
    setErrorMsg('');
  };

  useEffect(() => {
    fetchData();
  }, [filters, page, rowsPerPage]);

  return {
    data,
    error,
    loading,
    exportAnchor,
    exportItems,
    errorMsg,
    clearErrorMsg,
    openExportButton,
    closeExportButton,
    exportData,
    updateData,
    setPage,
    setRowsPerPage,
    page,
    rowsPerPage,
    totalCount,
  };
};

export default useTable;
